/**
 * BitacoraPost Component (Tier A)
 *
 * Full-width social-feed post for photos, comments, notes, and milestones.
 * Click image = lightbox; click elsewhere = inline expand with metadata.
 *
 * @module components/timeline/BitacoraPost
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Collapse } from 'react-bootstrap';
import TaskAssigneeAvatar from '../tasks/shared/TaskAssigneeAvatar';
import TimelineEventDetails from './TimelineEventDetails';
import BitacoraLightbox from './BitacoraLightbox';
import { humanizeAction, formatRelativeTime } from '../../services/timelineService';

const BitacoraPost = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const assignee = {
    id: event.user_id,
    full_name: event.user_name || 'Sistema',
  };

  // A chat message shared to the bitacora with an attached photo renders as a
  // full photo post — the photo is the evidence of progress (issue #267).
  const isPhoto =
    event.source === 'progress_photo' ||
    event.event_type === 'photo_uploaded' ||
    (event.event_type === 'chat_linked' && !!event.photo_url);
  const isMilestone = event.is_milestone || event.event_type === 'milestone_reached';
  const hasAttachments = event.metadata?.has_attachments;

  const handleCardClick = (e) => {
    // Ignore clicks on interactive children (image, links, buttons)
    if (e.target.closest('.bitacora-post-image, a, button')) return;
    setExpanded((v) => !v);
  };

  const handleImageClick = (e, src) => {
    e.stopPropagation();
    setLightboxSrc(src);
  };

  return (
    <>
      <article
        className={`bitacora-post${isMilestone ? ' bitacora-post-milestone' : ''}${expanded ? ' is-expanded' : ''}`}
        onClick={handleCardClick}
      >
        <div className="bitacora-post-avatar">
          <TaskAssigneeAvatar assignee={assignee} size="md" />
        </div>

        <div className="bitacora-post-body">
          <header className="bitacora-post-header">
            <span className="bitacora-post-author">{event.user_name || 'Sistema'}</span>
            <span className="bitacora-post-meta">
              <span className="bitacora-post-dot">·</span>
              {formatRelativeTime(event.timestamp)}
              <span className="bitacora-post-dot">·</span>
              <span className="bitacora-post-action">{humanizeAction(event)}</span>
              {event.metadata?.task_number && (
                <>
                  <span className="bitacora-post-dot">·</span>
                  <span className="bitacora-post-task">{event.metadata.task_number}</span>
                </>
              )}
            </span>
            {isMilestone && (
              <Badge bg="warning" text="dark" className="bitacora-post-milestone-badge">
                <i className="bi bi-flag-fill me-1"></i>
                Hito
              </Badge>
            )}
          </header>

          {event.description && (
            <p className="bitacora-post-text">{event.description}</p>
          )}

          {isPhoto && event.photo_url && (
            <div className="bitacora-post-image-wrap">
              <img
                src={event.photo_url}
                alt={event.description || 'Foto de la bitácora'}
                className="bitacora-post-image"
                onClick={(e) => handleImageClick(e, event.photo_url)}
                loading="lazy"
              />
            </div>
          )}

          {hasAttachments && !expanded && (
            <div className="bitacora-post-hint">
              <i className="bi bi-paperclip me-1"></i>
              Adjuntos · clic para ver
            </div>
          )}

          {!expanded ? (
            <div className="bitacora-post-footer">
              <button
                type="button"
                className="bitacora-post-expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(true);
                }}
              >
                <i className="bi bi-chevron-down me-1"></i>
                Ver detalles
              </button>
            </div>
          ) : (
            <div className="bitacora-post-footer">
              <button
                type="button"
                className="bitacora-post-expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(false);
                }}
              >
                <i className="bi bi-chevron-up me-1"></i>
                Ocultar detalles
              </button>
            </div>
          )}

          <Collapse in={expanded}>
            <div>
              {expanded && (
                <TimelineEventDetails event={event} hideImage={isPhoto} />
              )}
            </div>
          </Collapse>
        </div>
      </article>

      {lightboxSrc && (
        <BitacoraLightbox
          src={lightboxSrc}
          alt={event.description || 'Foto de la bitácora'}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </>
  );
};

BitacoraPost.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    source: PropTypes.string,
    event_type: PropTypes.string,
    description: PropTypes.string,
    user_id: PropTypes.string,
    user_name: PropTypes.string,
    timestamp: PropTypes.string,
    photo_url: PropTypes.string,
    is_milestone: PropTypes.bool,
    metadata: PropTypes.object,
  }).isRequired,
};

export default BitacoraPost;
